import React, { useRef, useEffect, useMemo, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import 'videojs-errors';
import './videoPlayer.css';
import { Box, CircularProgress } from '@mui/material';
import { useUpdateEffect } from 'react-use';
import useZoom from './useZoom';
import { registerSourceHandler } from './hlsPlugin';

registerSourceHandler(videojs);

function HLSPlayer({
  paused = false,
  enableZoom = false,
  onZoomChange,
  zoom = 1,
  children,
  setVideoPlayer,
  widescreen,
  authorization,
  handleEnded,
  onErrorPlaylist,
  live,
  thumbnail,
  id,
  ...props
}: videojs.PlayerOptions & {
  live?: boolean;
  fill?: boolean;
  widescreen?: boolean;
  noControlBar?: boolean;
  paused?: boolean;
  enableZoom?: boolean;
  onZoomChange?: (zoom: number) => void;
  zoom?: number;
  children?: (test: string) => React.ReactNode;
  setVideoPlayer?: (player: VideoJsPlayer | null) => void;
  authorization?: string;
  handleEnded?: () => void;
  onErrorPlaylist?: () => void;
  thumbnail?: string | null;
  id: string;
}) {
  const videoEl = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<VideoJsPlayer>();
  const retry = useRef(0);
  const retryTimeoutId = useRef<NodeJS.Timeout | 0>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useZoom({
    videoEl,
    zoom,
    enableZoom,
    onZoomChange,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleError(e: any) {
    e.stopImmediatePropagation();
    if (props.sources?.some(({ src }) => !!src)) {
      if (retry.current < 10) {
        retryTimeoutId.current = setTimeout(() => {
          if (!playerRef.current) return;
          const videoError = playerRef.current.error();
          retry.current += 1;
          // eslint-disable-next-line no-console
          console.error(`Retrying ${retry.current}`, videoError);
          playerRef.current.reset();
          playerRef.current.src(props.sources as videojs.Tech.SourceObject[]);
        }, 2000);
      } else setError('Video error');
    }
  }

  useEffect(
    function addAuthorization() {
      if (authorization) {
        // @ts-ignore
        // eslint-disable-next-line func-names
        videojs.Hls.xhr.beforeRequest = function (options) {
          // eslint-disable-next-line no-param-reassign
          options.headers = {
            ...options.headers,
            authorization,
          };
          return JSON.parse(JSON.stringify(options));
        };
      }
    },
    [authorization],
  );

  useEffect(function createPlayer() {
    // @ts-ignore https://docs.videojs.com/tutorial-options.html#useractions
    playerRef.current = videojs(videoEl.current, {
      ...{
        ...props,
        sources: undefined,
        inactivityTimeout: 0,
        errorDisplay: false,
      },
      html5: {
        vhs: {
          overrideNative: false,
          handlePartialData: true,
        },
        hlsjsConfig: {
          progressive: true,
          backBufferLength: live ? 0 : 100,
          maxBufferLength: live ? 10 : 185,
          // maxBufferHole: 1.5,
          startFragPrefetch: true,
          manifestLoadingMaxRetry: 10,
          manifestLoadingTimeOut: 20000,
          nudgeMaxRetry: 6,
          // initialLiveManifestSize: live ? 2 : 1,
          liveSyncDurationCount: 8,
          manifestLoadingRetryDelay: 300,
          levelLoadingRetryDelay: 300,
          fragLoadingRetryDelay: 300,
          fetchSetup: authorization
            ? // @ts-ignore
              (context, initParams) =>
                new Request(context.url, {
                  ...initParams,
                  headers: new Headers({
                    authorization,
                  }),
                })
            : undefined,
        },
      },
      // @ts-ignore
      userActions: {
        doubleClick: false,
        hotkeys: {
          playPauseKey: () => {},
        },
      },
    });
    playerRef.current?.ready(function r() {
      if (props.sources && playerRef.current) {
        playerRef.current.src(props.sources[0]);
      }
    });
    if (props.noControlBar) {
      const controlBar = playerRef.current?.getChild('controlBar');
      if (controlBar) playerRef.current?.removeChild(controlBar);
    }

    playerRef.current?.on('error', handleError);
    playerRef.current?.on('playing', () => {
      setLoading(false);
    });
    // https://github.com/videojs/video.js/issues/5435#issuecomment-471998039
    playerRef.current?.tech().on('retryplaylist', () => {
      onErrorPlaylist && onErrorPlaylist();
    });

    setVideoPlayer && setVideoPlayer(playerRef.current || null);

    return () => {
      setTimeout(() => {
        retryTimeoutId.current && clearTimeout(retryTimeoutId.current);
        playerRef.current && playerRef.current.dispose();
      }, 1000);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(
    function changeSource() {
      if (playerRef.current && props.sources) {
        playerRef.current.src(props.sources[0]);
      }
    },
    [props.sources],
  );

  useEffect(
    function handlePlayPause() {
      if (!playerRef.current) {
        return;
      }
      paused ? playerRef.current.pause() : playerRef.current.play();
    },
    [paused],
  );

  useEffect(
    function handleMuted() {
      if (!playerRef.current) {
        return;
      }

      playerRef.current.muted(!!props.muted);
    },
    [props.muted],
  );

  const className = useMemo(() => {
    const prefix = 'video-js';
    if (props.fill) return `${prefix} vjs-fill`;
    return prefix;
  }, [props.fill]);

  if (error) throw new Error(error);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <div data-vjs-player>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoEl} className={className} />
        {loading && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%', height: '100%' }}
            >
              <CircularProgress />
            </Box>
          </>
        )}
      </div>
    </Box>
  );
}

export default HLSPlayer;
